import cx from 'classnames';
import { format } from 'date-fns';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useStores } from 'core';
import { LocaleIdType } from 'locales';
import { LocalizedMessage } from 'shared/components/other';
import { ReportData } from 'types/reportConstructor';

import classes from './ReportTable.module.scss';
import { reportActionsRoles } from '../../../../Auth/helpers/roles';
import { BasicRole } from '../../../../../types/profile';

const formatDate = (date: number | Date) => format(date, 'dd.MM.yyyy');

export const ReportTable: React.FC = observer(() => {
  const navigate = useNavigate();
  const {
    reportListStore: store,
    authStore: { profile },
  } = useStores();
  const { data, fields, sorting, loadData, cleanUp, updateSorting } = store;

  const isActionDisabled = useCallback(
    (authorKeycloakId: string | null): boolean => {
      const isAuthor = authorKeycloakId ? profile?.id === authorKeycloakId : false;
      const hasAllowedRole = profile?.roles?.some((role) => reportActionsRoles.includes(role as BasicRole)) || false;
      return !hasAllowedRole && !isAuthor;
    },
    [profile?.id, profile?.roles],
  );

  const handleOpen = useCallback(
    (id: number) => {
      navigate(`/report-constructor/${id}/view`);
    },
    [navigate],
  );

  const handleEdit = useCallback(
    (id: number) => {
      navigate(`/report-constructor/${id}/edit`);
    },
    [navigate],
  );

  const actions = useMemo(
    () => [
      { name: 'edit', icon: 'pi-file-edit', handler: handleEdit, isDisabled: isActionDisabled },
      { name: 'open', icon: 'pi-file', handler: handleOpen, isDisabled: false },
      { name: 'copy', icon: 'pi-copy', handler: store.copy, isDisabled: false },
      { name: 'export', icon: 'pi-file-export', handler: store.export, isDisabled: false },
      { name: 'delete', icon: 'pi-trash', handler: store.delete, isDisabled: isActionDisabled },
    ],
    [store, isActionDisabled, handleOpen, handleEdit],
  );

  const handleSortButtonClick = useCallback(
    (e: React.MouseEvent<HTMLElement>): void => {
      const target = e.target as HTMLElement;
      const sortingField = target?.closest<HTMLElement>('[data-sort]')?.dataset.sort as keyof ReportData;

      if (sortingField) {
        updateSorting(sortingField);
      }
    },
    [updateSorting],
  );

  useEffect(() => {
    loadData();

    return () => {
      cleanUp();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (data.length === 0) {
    return null;
  }

  return (
    <table className={classes.root}>
      <thead>
        <tr onClick={handleSortButtonClick}>
          {fields.map(({ name, isSorting }) => {
            const isSortingField = sorting?.sortingField === name;

            return (
              <th key={name} data-sort={isSorting ? name : undefined} data-sort-active={isSortingField}>
                <LocalizedMessage id={`report-list.fields.${name}` as LocaleIdType} />
                {isSorting && (
                  <i
                    className={cx(classes.sortingIcon, 'icon icon-arrow-down', {
                      [classes.sortingIconReverse]: isSortingField && sorting?.sortingType === 'ASC',
                    })}
                  />
                )}
              </th>
            );
          })}
          <th>&nbsp;</th>
        </tr>
      </thead>
      <tbody>
        {data.map(
          ({
            id,
            name,
            createdDate,
            statisticDateStart,
            statisticDateEnd,
            userCreated,
            userModified,
            authorKeycloakId,
          }: ReportData) => (
            <tr key={id} className={classes.row}>
              <td>{name}</td>
              <td>{createdDate && formatDate(createdDate)}</td>
              <td>
                {statisticDateStart && formatDate(statisticDateStart)}
                {statisticDateEnd && ` - ${formatDate(statisticDateEnd)}`}
              </td>
              <td>{userCreated}</td>
              <td>{userModified}</td>
              <td>
                <div className={classes.actions}>
                  {actions.map(({ name: actionName, icon, handler, isDisabled }) => {
                    const isIconDisabled = typeof isDisabled === 'function' ? isDisabled(authorKeycloakId) : isDisabled;
                    return (
                      <LocalizedMessage key={actionName} id={`report-list.actions.${actionName}` as LocaleIdType}>
                        {(title: string) => (
                          <i
                            className={cx(`pi ${icon}`, classes.action, { [classes.actionDisabled]: isIconDisabled })}
                            title={title}
                            data-test={`${actionName}-report-${id}`}
                            onClick={isIconDisabled ? undefined : () => handler(id, name)}
                          />
                        )}
                      </LocalizedMessage>
                    );
                  })}
                </div>
              </td>
            </tr>
          ),
        )}
      </tbody>
    </table>
  );
});
